import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, ArrowUpDown, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

type SortType = 'alphabetical' | 'reverse-alphabetical' | 'length-asc' | 'length-desc' | 'random';

const TextSorter: React.FC = () => {
  const [text, setText] = useState('');
  const [sortType, setSortType] = useState<SortType>('alphabetical');
  const [removeEmpty, setRemoveEmpty] = useState(true);
  const [caseSensitive, setCaseSensitive] = useState(false);

  // SEO optimization
  useSEO({
    title: 'Text Sorter - Sort Lines Alphabetically & More | SnapTools',
    description: 'Free text sorter tool. Sort lines alphabetically, by length, or randomly. Case-sensitive options and empty line removal. Instant text sorting.',
    keywords: 'text sorter, sort lines, alphabetical sort, sort by length, random sort, line sorter, text organizer'
  });

  const sortLines = () => {
    if (!text.trim()) return '';
    
    let lines = text.split('\n');
    
    if (removeEmpty) {
      lines = lines.filter(line => line.trim() !== '');
    }
    
    switch (sortType) {
      case 'alphabetical':
        return lines.sort((a, b) => {
          if (caseSensitive) {
            return a.localeCompare(b);
          }
          return a.toLowerCase().localeCompare(b.toLowerCase());
        }).join('\n');
        
      case 'reverse-alphabetical':
        return lines.sort((a, b) => {
          if (caseSensitive) {
            return b.localeCompare(a);
          }
          return b.toLowerCase().localeCompare(a.toLowerCase());
        }).join('\n');
        
      case 'length-asc':
        return lines.sort((a, b) => a.length - b.length).join('\n');
        
      case 'length-desc':
        return lines.sort((a, b) => b.length - a.length).join('\n');
        
      case 'random':
        const shuffled = [...lines];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled.join('\n');
        
      default:
        return lines.join('\n');
    }
  };

  const sortedText = sortLines();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(sortedText).then(() => {
      toast.success('Sorted text copied to clipboard');
    });
  };

  const clearText = () => {
    setText('');
    toast.success('Text cleared successfully');
  };

  const sortOptions = [
    { value: 'alphabetical', label: 'Alphabetical (A-Z)' },
    { value: 'reverse-alphabetical', label: 'Reverse Alphabetical (Z-A)' },
    { value: 'length-asc', label: 'Length (Shortest First)' },
    { value: 'length-desc', label: 'Length (Longest First)' },
    { value: 'random', label: 'Random Order' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-12">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
            <ArrowUpDown className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4 px-2">
            Text Sorter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Sort lines of text in various orders - alphabetically, by length, or randomly. Perfect for organizing lists and data.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Input Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 mb-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#8438ff]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#8438ff]">Enter Your Text</h2>
                </div>
                <div className="flex space-x-2">
                  {text && (
                    <button
                      onClick={clearText}
                      className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Clear text"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Enter each line of text you want to sort..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
              />
            </motion.div>

            {/* Output Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <ArrowUpDown className="h-5 w-5 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Sorted Text</h2>
                </div>
                <button
                  onClick={copyToClipboard}
                  disabled={!sortedText}
                  className="px-4 py-2 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] disabled:bg-gray-300 transition-colors"
                >
                  <Copy className="h-4 w-4 mr-2 inline" />
                  Copy Result
                </button>
              </div>
              
              <textarea
                value={sortedText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl resize-none bg-gray-50"
                placeholder="Sorted text will appear here..."
              />
              
              {text && (
                <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  <p><strong>Original lines:</strong> {text.split('\n').length}</p>
                  <p><strong>Sorted lines:</strong> {sortedText.split('\n').filter(line => line.trim() !== '').length}</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Settings Panel */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-8 sticky top-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#8438ff]/10 rounded-xl flex items-center justify-center">
                  <Settings className="h-5 w-5 text-[#8438ff]" />
                </div>
                <h2 className="text-xl font-bold text-[#8438ff]">Sort Options</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">Sort Type</label>
                  <select
                    value={sortType}
                    onChange={(e) => setSortType(e.target.value as SortType)}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  >
                    {sortOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={removeEmpty}
                      onChange={(e) => setRemoveEmpty(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Remove empty lines</span>
                      <p className="text-xs text-gray-500">Filter out blank lines from the result</p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={caseSensitive}
                      onChange={(e) => setCaseSensitive(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-[#8438ff] focus:ring-[#8438ff]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Case sensitive sorting</span>
                      <p className="text-xs text-gray-500">Distinguish between uppercase and lowercase</p>
                    </div>
                  </label>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-12"
        />
      </div>
    </div>
  );
};

export default TextSorter;
